/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { HeaderMiniCartDropMenu } from '@/components/content/Header/parts/MiniCartDropMenu';
import { headerItemLinkSX } from '@/components/content/Header/styles/itemLink';
import { headerItemStackSX } from '@/components/content/Header/styles/itemStack';
import { headerMiniCartBadgeSX } from '@/components/content/Header/styles/miniCart/badge';
import { headerNavBarDropMenuSX } from '@/components/content/Header/styles/navBar/dropMenu';
import { useCart } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { GENERIC_USER_ID } from '@/data/constants/user';
import { useDeviceType } from '@/data/deviceDetection';
import { areArraysEqual } from '@/data/utils/areArraysEqual';
import { getCookie, setCookie } from '@/utils/clickAndCollect';
import { Switch } from '@/utils/switch';
import { touchMoveListener } from '@/utils/touchMoveListener';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import {
	Badge,
	Breakpoint,
	ClickAwayListener,
	Stack,
	Tooltip,
} from '@mui/material';
import { FC, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';

type Props = {
	mobileBreakpoint: Breakpoint;
};

export const HeaderMiniCart: FC<Props> = ({ mobileBreakpoint = 'sm' }) => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const RouteLocal = useLocalization('Routes');
	const CartLabels = useLocalization('MiniCart');
	const { message } = useNotifications();
	const { user: { userId } = {} } = useUser();
	const { count: itemCount, mutateCart, data } = useCart();
	const { isMobile } = useDeviceType();
	const [open, setOpen] = useState(false);
	const initialized = useRef<boolean>(false);
	const [count, setCount] = useState<number>(0)
	const handleToolTip = useCallback(
		(action?: string) => () =>
			 setOpen((open) =>
					Switch(action)
						.case('open', () => true)
						.case('close', () => false)
						.defaultTo(() => !open)
					),
		[]
	);

	const onMouseEnter: MouseEventHandler<HTMLDivElement> = useCallback(() => {
		if (!isMobile) {
			handleToolTip('open')();
		}
	}, [handleToolTip, isMobile]);

	useEffect(() => {
		if (message.isAddToCart) {
			setOpen((prev)=>!prev?true:prev);
		}
	}, [message.isAddToCart]);

	useEffect(() => {
		if (!router.asPath) return;
		handleToolTip('close')();
	}, [handleToolTip, router.asPath]);

	useEffect(() => {
		if (initialized.current 
				&& userId!==GENERIC_USER_ID // mutateCart not needed for generic user, it does called in some scenarios. 
											// This condition will handle those scenarios
		
		) {
			mutateCart();
		} else {
			initialized.current = true;
		}
	}, [mutateCart, userId]);

	// for CDN caching -- render this on client
	useEffect(() => {
		setCount(() => itemCount);
	}, [itemCount]);

	useEffect(() => touchMoveListener(handleToolTip('close')), []); // eslint-disable-line react-hooks/exhaustive-deps

	const isHelloRetailSearchEnabled = settings?.userData?.HelloRetailSearchEnabled === "true";
	useEffect(() => {
		if (isHelloRetailSearchEnabled &&
			typeof window !== "undefined" && window.hrq?.push) {
			const partNumbers = data?.orderItem?.map(oi => oi.xitem_parentPartNumber) ?? [];
			const partNumbersC = JSON.parse(getCookie("PARTNUMBERS_IN_CART") ?? '[]') as string[];
			
			if (
				(partNumbersC.length === 0 && partNumbers.length !== 0) ||
				!areArraysEqual(partNumbers, partNumbersC)
			) {
				const hrq = window.hrq || [];
				hrq.push([
					"setCart",
					{
						total: parseFloat(data?.grandTotal ?? '0.0').toFixed(2),
						productNumbers: partNumbers,
					}
				]);
				setCookie("PARTNUMBERS_IN_CART", JSON.stringify(partNumbers));
			}
		}

	}, [data]);

	return (
		<ClickAwayListener onClickAway={handleToolTip('close')}>
			<Stack onMouseLeave={handleToolTip('close')}>
				<Tooltip
					placement="bottom"
					open={open}
					onClose={handleToolTip('close')}
					disableFocusListener
					disableHoverListener
					disableTouchListener
					componentsProps={{
						tooltip: {
							sx: headerNavBarDropMenuSX,
						},
					}}
					title={
						<Stack direction="row">
							<HeaderMiniCartDropMenu {...{ setOpen}}/>
						</Stack>
					}
				>
					<Stack alignItems="center" onMouseEnter={onMouseEnter} sx={headerItemStackSX}>
						<Badge badgeContent={count} color="secondary" sx={headerMiniCartBadgeSX}>
							<Linkable
								id="header-mini-cart-icon"
								href={`/${RouteLocal.Cart.route.t()}`}
								data-testid="header-mini-cart-icon"
								aria-label={count === 0 ? CartLabels.miniCartLabel.t() : CartLabels.Items.t({count})}
								sx={headerItemLinkSX}
							>
								<ShoppingBasketOutlinedIcon sx={{fontSize:24}} />
							</Linkable>
						</Badge>
						{/* <Typography sx={headerIconLabelSX}>
							<Linkable
								href={`/${RouteLocal.Cart.route.t()}`}
								id="header-mini-cart-title"
								data-testid="header-mini-cart-title"
								aria-label={CartLabels.Items.t({ count })}
								sx={headerItemLinkSX}
							>
								{CartLabels.Items.t({ count })}
							</Linkable>
						</Typography> */}
					</Stack>
				</Tooltip>
				{/* {isMobile === false ? (
					<Button sx={getAdditive('coverTapTarget')} onClick={handleToolTip()}>
						{CartLabels.Title.t()}
					</Button>
				) : null} */}
			</Stack>
		</ClickAwayListener>
	);
};
